// @ts-nocheck
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {BaseButton, BaseEmpty, BaseModal, BaseSpinner, BaseTable, BaseTag} from "@/components";
import useGetLotteryResources from "../../hooks/useGetLotteryResources";
import useGetLotteryTickets from "./hooks/useGetLotteryTickets";

const myTicketsTableWrapperCls = "w-full max-w-[1200px] mx-auto px-2 max-[900px]:max-w-full max-[900px]:px-1";
const ticketItemsRowCls =
  "w-full max-[900px]:[&_.ant-col]:flex-[0_0_33.3333%] max-[900px]:[&_.ant-col]:max-w-[33.3333%] max-[600px]:[&_.ant-col]:flex-[0_0_50%] max-[600px]:[&_.ant-col]:max-w-[50%] max-[400px]:[&_.ant-col]:flex-[0_0_100%] max-[400px]:[&_.ant-col]:max-w-full";

const MyTickets = () => {
  const {
    data: ticketsData,
    isLoading: isLoadingTickets,
    isSuccess: isSuccessTickets,
    error: ticketsError,
  } = useGetLotteryTickets();

  // Fetch resources to get fichas data (for displaying animal/figure images)
  const {fichas, isLoading: isLoadingResources} = useGetLotteryResources(1);

  const [tickets, setTickets] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useTranslation();

  dayjs.extend(relativeTime);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    if (isSuccessTickets && ticketsData?.data?.data?.tickets) {
      const sortedTickets = [...ticketsData.data.data.tickets].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setTickets(sortedTickets);
    }
  }, [ticketsData, isSuccessTickets]);

  const formatDate = dateString => {
    return dayjs(dateString).format("DD/MM/YYYY HH:mm:ss");
  };

  const formatTime = timeString => {
    if (!timeString) return "N/A";
    // Convert "19:00:00" to "7:00 PM"
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Helper functions to get ficha info
  const findFichaNombre = fichaId => {
    if (!fichas || !Array.isArray(fichas)) return "N/A";
    const ficha = fichas.find(f => f.id === fichaId || f.numero === fichaId || f.id === String(fichaId));
    return ficha ? ficha.nombre : `#${fichaId}`;
  };

  const getFichaImage = fichaId => {
    if (!fichas || !Array.isArray(fichas)) return null;
    const ficha = fichas.find(f => f.id === fichaId || f.numero === fichaId || f.id === String(fichaId));
    return ficha ? ficha.img_f : null;
  };

  const showModal = ticket => {
    setSelectedTicket(ticket);
    setModalVisible(true);
  };

  const handleOk = () => {
    setModalVisible(false);
    setSelectedTicket(null);
  };

  const isLoading = isLoadingTickets || isLoadingResources;

  if (isLoading) {
    return (
      <div style={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh"}}>
        <BaseSpinner size="lg" />
      </div>
    );
  }

  if (ticketsError) {
    return <div>Error: {ticketsError.message}</div>;
  }

  if (isSuccessTickets && tickets.length === 0) {
    return (
      <div>
        <BaseButton
          variant="primary"
          onClick={() => navigate("/lottery")}
          style={{margin: "1rem"}}
        >
          {t("lotteryBackBtn") || "Volver"}
        </BaseButton>
        <BaseEmpty description={t("lotteryModalNoTickets") || "No hay tickets"} />
      </div>
    );
  }

  const isTicketLoser = ticket => {
    const firstItem = ticket.items?.[0];

    // Venezuelan timezone is UTC-4
    const VENEZUELA_OFFSET_HOURS = -4;

    // Parse draw time by combining draw_time (HH:mm:ss) with the ticket creation date
    let drawDateTime = null;
    if (firstItem?.draw_time) {
      // Parse the creation date and adjust to Venezuelan time
      const createdDate = dayjs(ticket.created_at);
      const createdDateVenezuela = createdDate.add(VENEZUELA_OFFSET_HOURS, "hour");

      // Get the date part in Venezuelan timezone
      const dateStr = createdDateVenezuela.format("YYYY-MM-DD");

      // Combine date with draw time to create full datetime string
      const drawDateTimeStr = `${dateStr} ${firstItem.draw_time}`;
      drawDateTime = dayjs(drawDateTimeStr);

      // If the draw time is before the creation time (in Venezuelan timezone),
      // it means the draw is for the next day
      if (drawDateTime.isBefore(createdDateVenezuela)) {
        drawDateTime = drawDateTime.add(1, "day");
      }
    }

    // Get current time adjusted to Venezuelan timezone
    const nowVenezuela = dayjs().add(VENEZUELA_OFFSET_HOURS, "hour");

    const isOld = drawDateTime
      ? drawDateTime.isBefore(nowVenezuela.subtract(1, "hour"))
      : dayjs(ticket.created_at).add(VENEZUELA_OFFSET_HOURS, "hour").isBefore(nowVenezuela.subtract(24, "hour"));

    const result = ticket.is_looser || (!ticket.is_winner && isOld);

    return result;
  };

  const columns = [
    {
      key: "ticket_code",
      header: t("lotteryModalCode") || "Código",
      render: ticket => (
        <div>
          <strong>{ticket.ticket_code}</strong>
          <br />
          <BaseTag variant={ticket.is_winner ? "success" : "danger"}>
            {ticket.is_winner
              ? t("lotteryGameWinner") || "Ganador"
              : isTicketLoser(ticket)
                ? t("lotteryGamePrize") || "Perdedor"
                : "En Proceso"}
          </BaseTag>
        </div>
      ),
    },
    {
      key: "created_at",
      header: t("lotteryModalDate") || "Fecha",
      render: ticket => <span>{formatDate(ticket.created_at)}</span>,
    },
    {
      key: "game",
      header: "Juego",
      render: ticket => {
        const firstItem = ticket.items && ticket.items.length > 0 ? ticket.items[0] : null;
        const isLegacy = firstItem?.draw_id === "00000000-0000-0000-0000-000000000000";
        return <span>{isLegacy ? "Ticket Antiguo" : ticket.game_name || "N/A"}</span>;
      },
    },
    {
      key: "draw",
      header: "Sorteo",
      render: ticket => {
        const firstItem = ticket.items && ticket.items.length > 0 ? ticket.items[0] : null;
        const isLegacy = firstItem?.draw_id === "00000000-0000-0000-0000-000000000000";
        if (isLegacy) return <span>-</span>;
        return <span>{firstItem?.draw_time ? formatTime(firstItem.draw_time) : "N/A"}</span>;
      },
    },
    {
      key: "total_amount",
      header: t("lotteryModalTotal") || "Total",
      render: ticket => <strong>{ticket.total_amount} Bs</strong>,
    },
  ];

  return (
    <div style={{padding: "1rem"}}>
      <h2 style={{textAlign: "center", marginBottom: "1rem", fontSize: "var(--font-h1)", fontWeight: 700}}>
        {t("lotteryMyTickets") || "Mis Tickets"}
      </h2>

      <BaseButton
        variant="primary"
        onClick={() => navigate("/lottery")}
        style={{marginBottom: "1rem"}}
      >
        {t("lotteryBackBtn") || "Volver"}
      </BaseButton>

      <div className={myTicketsTableWrapperCls}>
        <BaseTable
          data={tickets}
          columns={columns}
          rowKey="id"
          onRowClick={showModal}
        />
      </div>

      <BaseModal
        title={`Ticket ${selectedTicket?.ticket_code}`}
        open={modalVisible}
        onClose={handleOk}
        size="xl"
        footer={
          <BaseButton
            variant="primary"
            onClick={handleOk}
          >
            Cerrar
          </BaseButton>
        }
      >
        {selectedTicket && (
          <div>
            {/* Ticket Header Info */}
            <div
              className="baseCard"
              style={{marginBottom: "1rem"}}
            >
              <div style={{display: "flex", flexWrap: "wrap", gap: "var(--space-4-5)"}}>
                <div style={{flex: "1 1 45%", minWidth: "200px"}}>
                  <span style={{fontWeight: "bold"}}>CÃ³digo:</span> <span>{selectedTicket.ticket_code}</span>
                </div>
                <div style={{flex: "1 1 45%", minWidth: "200px"}}>
                  <span style={{fontWeight: "bold"}}>Fecha:</span> <span>{formatDate(selectedTicket.created_at)}</span>
                </div>
                <div style={{flex: "1 1 45%", minWidth: "200px"}}>
                  <span style={{fontWeight: "bold"}}>Total:</span> <span>{selectedTicket.total_amount} Bs</span>
                </div>
                <div style={{flex: "1 1 45%", minWidth: "200px"}}>
                  <span style={{fontWeight: "bold"}}>Premio:</span> <span>{selectedTicket.winning_amount} Bs</span>
                </div>
                <div style={{flex: "1 1 100%"}}>
                  <BaseTag
                    variant={selectedTicket.is_winner ? "success" : "danger"}
                    style={{fontSize: "var(--font-body-lg)"}}
                  >
                    {selectedTicket.is_winner ? "GANADOR" : isTicketLoser(selectedTicket) ? "PERDEDOR" : "EN PROCESO"}
                  </BaseTag>
                </div>
              </div>
            </div>

            {/* Items (Draws) */}
            {selectedTicket.items && selectedTicket.items.length > 0 ? (
              selectedTicket.items.map((item, itemIndex) => {
                const isLegacy = item.draw_id === "00000000-0000-0000-0000-000000000000";

                return (
                  <div
                    className="baseCard"
                    key={item.id || itemIndex}
                    style={{marginBottom: "1rem"}}
                    title={
                      isLegacy ? (
                        <div>
                          <strong>Ticket Antiguo</strong>
                          <div style={{fontSize: "var(--font-body)", color: "#888"}}>Sin informaciÃ³n de sorteo</div>
                        </div>
                      ) : (
                        <div style={{display: "flex", alignItems: "center", gap: "1rem"}}>
                          {item.game_logo && (
                            <img
                              src={`${item.game_logo.startsWith("http") ? "" : "https://images.cdn.raspaygana.com.ve/"}${item.game_logo}`}
                              alt={selectedTicket.game_name}
                              style={{height: "var(--size-10)", objectFit: "contain"}}
                              onError={e => {
                                e.target.style.display = "none";
                              }}
                            />
                          )}
                          <div>
                            <div>
                              <strong>{selectedTicket.game_name || "Juego"}</strong>
                            </div>
                            <div style={{fontSize: "var(--font-body)", color: "#888"}}>
                              {item.draw_name || ""} {item.draw_time ? `- ${formatTime(item.draw_time)}` : ""}
                            </div>
                          </div>
                        </div>
                      )
                    }
                  >
                    {/* Jugadas (new API structure) */}
                    {item.jugadas && item.jugadas.length > 0 ? (
                      <>
                        {/* Accepted Bets */}
                        {(() => {
                          // Detect rejected bets: either status is "rejected" OR monto is 0 (when status is empty/undefined)
                          const isRejected = j =>
                            j.status === "rejected" || (j.monto === 0 && (!j.status || j.status === ""));
                          const acceptedBets = item.jugadas.filter(j => !isRejected(j));
                          const rejectedBets = item.jugadas.filter(j => isRejected(j));

                          return (
                            <>
                              {acceptedBets.length > 0 && (
                                <div
                                  style={{display: "flex", flexWrap: "wrap", gap: "var(--space-4-5)"}}
                                  className={ticketItemsRowCls}
                                >
                                  {acceptedBets.map((jugada, jugadaIndex) => {
                                    const fichaImage = getFichaImage(jugada.ficha_id);
                                    const fichaNombre = jugada.figure_name || findFichaNombre(jugada.ficha_id);

                                    return (
                                      <div
                                        key={jugada.id || jugadaIndex}
                                        span={6}
                                      >
                                        <div
                                          className="baseCard"
                                          size="small"
                                          style={{
                                            textAlign: "center",
                                            backgroundColor: "var(--color-surface-light)",
                                            border: "1px solid #d9d9d9",
                                          }}
                                        >
                                          {fichaImage ? (
                                            <div>
                                              <img
                                                src={fichaImage}
                                                alt={fichaNombre}
                                                style={{
                                                  width: "var(--size-15)",
                                                  height: "var(--size-15)",
                                                  objectFit: "contain",
                                                }}
                                                onError={e => {
                                                  e.target.style.display = "none";
                                                }}
                                              />
                                              <div
                                                style={{
                                                  fontSize: "var(--font-caption)",
                                                  color: "#666",
                                                  marginTop: "var(--space-1)",
                                                }}
                                              >
                                                {fichaNombre}
                                              </div>
                                            </div>
                                          ) : (
                                            <div>
                                              <div
                                                style={{
                                                  fontSize: "var(--font-h1)",
                                                  fontWeight: "bold",
                                                  color: "#1890ff",
                                                }}
                                              >
                                                {jugada.ficha_id || "?"}
                                              </div>
                                              {fichaNombre && (
                                                <div
                                                  style={{
                                                    fontSize: "var(--font-caption)",
                                                    color: "#666",
                                                    marginTop: "var(--space-1)",
                                                  }}
                                                >
                                                  {fichaNombre}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                          <div
                                            style={{
                                              fontSize: "var(--font-body)",
                                              color: "#000",
                                              fontWeight: "bold",
                                              marginTop: "var(--space-1)",
                                            }}
                                          >
                                            {jugada.monto ? `${jugada.monto} Bs` : "N/A"}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Rejected Bets Section */}
                              {rejectedBets.length > 0 && (
                                <div style={{marginTop: "1rem"}}>
                                  <span
                                    style={{
                                      fontWeight: "bold",
                                      color: "var(--color-danger-text)",
                                      display: "block",
                                      marginBottom: "var(--space-2-5)",
                                    }}
                                  >
                                    Apuestas No Aceptadas ({rejectedBets.length})
                                  </span>
                                  <div
                                    style={{display: "flex", flexWrap: "wrap", gap: "var(--space-4-5)"}}
                                    className={ticketItemsRowCls}
                                  >
                                    {rejectedBets.map((jugada, jugadaIndex) => {
                                      const fichaImage = getFichaImage(jugada.ficha_id);
                                      const fichaNombre = jugada.figure_name || findFichaNombre(jugada.ficha_id);

                                      return (
                                        <div
                                          key={jugada.id || `rejected-${jugadaIndex}`}
                                          span={6}
                                        >
                                          <div
                                            className="baseCard"
                                            size="small"
                                            style={{
                                              textAlign: "center",
                                              backgroundColor: "#fff2f0",
                                              border: "1px solid #ffccc7",
                                            }}
                                          >
                                            {fichaImage ? (
                                              <div style={{opacity: 0.6}}>
                                                <img
                                                  src={fichaImage}
                                                  alt={fichaNombre}
                                                  style={{
                                                    width: "var(--size-15)",
                                                    height: "var(--size-15)",
                                                    objectFit: "contain",
                                                    filter: "grayscale(50%)",
                                                  }}
                                                  onError={e => {
                                                    e.target.style.display = "none";
                                                  }}
                                                />
                                                <div
                                                  style={{
                                                    fontSize: "var(--font-caption)",
                                                    color: "#666",
                                                    marginTop: "var(--space-1)",
                                                  }}
                                                >
                                                  {fichaNombre}
                                                </div>
                                              </div>
                                            ) : (
                                              <div style={{opacity: 0.6}}>
                                                <div
                                                  style={{
                                                    fontSize: "var(--font-h1)",
                                                    fontWeight: "bold",
                                                    color: "#cf1322",
                                                  }}
                                                >
                                                  {jugada.ficha_id || "?"}
                                                </div>
                                                {fichaNombre && (
                                                  <div
                                                    style={{
                                                      fontSize: "var(--font-caption)",
                                                      color: "#666",
                                                      marginTop: "var(--space-1)",
                                                    }}
                                                  >
                                                    {fichaNombre}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            <div
                                              style={{
                                                fontSize: "var(--font-body)",
                                                color: "#cf1322",
                                                fontWeight: "bold",
                                                marginTop: "var(--space-1)",
                                              }}
                                            >
                                              {jugada.requested_amount ? `${jugada.requested_amount} Bs` : "N/A"}
                                            </div>
                                            <BaseTag
                                              variant="danger"
                                              style={{marginTop: "var(--space-1)", fontSize: "var(--font-eyebrow)"}}
                                            >
                                              No aceptado
                                            </BaseTag>
                                            {jugada.rejection_reason && (
                                              <div
                                                style={{
                                                  fontSize: "var(--font-eyebrow)",
                                                  color: "#cf1322",
                                                  marginTop: "var(--space-1)",
                                                }}
                                              >
                                                {/* Remove "Code -XX:" prefix from rejection reason */}
                                                {jugada.rejection_reason.replace(/^Code\s*-?\d+:\s*/i, "")}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </>
                    ) : item.bets && item.bets.length > 0 ? (
                      // Fallback for old API structure (if any old tickets still use "bets")
                      <div
                        style={{display: "flex", flexWrap: "wrap", gap: "var(--space-4-5)"}}
                        className={ticketItemsRowCls}
                      >
                        {item.bets.map((bet, betIndex) => {
                          const fichaImage = getFichaImage(bet.ficha_id);
                          // Use figure_name from API if available, otherwise fallback to lookup
                          const fichaNombre = bet.figure_name || findFichaNombre(bet.ficha_id);

                          return (
                            <div
                              key={bet.id || betIndex}
                              span={6}
                            >
                              <div
                                className="baseCard"
                                size="small"
                                style={{
                                  textAlign: "center",
                                  backgroundColor: "var(--color-surface-light)",
                                  border: "1px solid #d9d9d9",
                                }}
                              >
                                {fichaImage ? (
                                  <div>
                                    <img
                                      src={fichaImage}
                                      alt={fichaNombre}
                                      style={{width: "var(--size-15)", height: "var(--size-15)", objectFit: "contain"}}
                                      onError={e => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                    <div
                                      style={{
                                        fontSize: "var(--font-caption)",
                                        color: "#666",
                                        marginTop: "var(--space-1)",
                                      }}
                                    >
                                      {fichaNombre}
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <div style={{fontSize: "var(--font-h1)", fontWeight: "bold", color: "#1890ff"}}>
                                      {bet.ficha_id || "?"}
                                    </div>
                                    {fichaNombre && (
                                      <div
                                        style={{
                                          fontSize: "var(--font-caption)",
                                          color: "#666",
                                          marginTop: "var(--space-1)",
                                        }}
                                      >
                                        {fichaNombre}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div
                                  style={{
                                    fontSize: "var(--font-body)",
                                    color: "#000",
                                    fontWeight: "bold",
                                    marginTop: "var(--space-1)",
                                  }}
                                >
                                  {bet.monto ? `${bet.monto} Bs` : "N/A"}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <BaseEmpty description="No hay jugadas" />
                    )}
                  </div>
                );
              })
            ) : (
              <BaseEmpty description="No hay informaciÃ³n del sorteo" />
            )}
          </div>
        )}
      </BaseModal>
    </div>
  );
};

export default MyTickets;
